import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import DataTable, {type IDataTableProps} from "./DataTable";
import {type IMenuAction} from "./ActionMenu";
import useDialogFocus from "./useDialogFocus";
import {normalizeLanguage} from "../i18n";

type DictionaryRecord = Record<string, any>;
type LocaleRow = {locale: string; name: string; short: string};
type ParentOption = {id: string; key: string; name: string; type: string};
export type DictionaryMetadataField = {
  path: string;
  bsonType?: string | string[];
  input?: "text" | "url" | "number" | "checkbox" | "tags" | "json";
  step?: string;
  description?: string;
};
export type DictionaryFormSchema = {
  type: string;
  fields?: Array<Record<string, any>>;
  metadataFields?: DictionaryMetadataField[];
};

interface DictionaryPanelProps extends Pick<IDataTableProps, "rows" | "datasetKey" | "userTimeZone" | "totalCount" | "serverFilter"> {
  resource: string;
  categories: string[];
  languages: string[];
  methods: string[];
  canCreate: boolean;
  createRequest: number;
  schemas: Record<string, DictionaryFormSchema>;
  onLoadSchema: (type: string) => Promise<DictionaryFormSchema>;
  onOpenCategory: (type: string) => void;
  onSave: (resource: string, payload: DictionaryRecord, creating: boolean) => Promise<void>;
  onDelete: (resource: string, record: DictionaryRecord) => Promise<void>;
  onFindParents: (type: string, search: string) => Promise<ParentOption[]>;
}

const PARENT_TYPES: Record<string, string[]> = {
  region: ["country"],
  city: ["region"],
  locality: ["city", "region"],
  district: ["city", "region"],
  street: ["city", "locality", "district"],
  airport: ["city"], railwayStation: ["city"], busStation: ["city"], port: ["city"],
  hotel: ["city"], roomType: ["accommodationType"],
  seatClass: ["vehicleType", "transportType"],
};

const emptyForm = (type = "") => ({
  type, key: "", locales: [{locale: "default", name: "", short: ""}] as LocaleRow[],
  parentType: PARENT_TYPES[type]?.[0] || "", parentId: "", parentSearch: "",
  tags: "", metadataValues: {} as Record<string, string | boolean>, status: "enabled", sortOrder: "0",
  effectiveFrom: "", effectiveTo: "",
});

export const dateInputValue = (value: any) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  // Build the datetime-local string from local-time getters (not toISOString,
  // which is UTC) so the displayed value round-trips through the browser's
  // own timezone instead of drifting by the UTC offset on every open/save.
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const valueAtPath = (value: Record<string, any>, path: string) =>
  path.split(".").reduce<any>((current, key) => current == null ? undefined : current[key], value);

const setAtPath = (target: Record<string, any>, path: string, value: any) => {
  const parts = path.split(".");
  let current = target;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) {
      if (value === undefined || value === "") delete current[part];
      else current[part] = value;
      return;
    }
    if (!current[part] || typeof current[part] !== "object" || Array.isArray(current[part])) current[part] = {};
    current = current[part];
  });
};

const inputValue = (value: any, field: DictionaryMetadataField): string | boolean => {
  if (field.input === "checkbox") return Boolean(value);
  if (field.input === "tags") return Array.isArray(value) ? value.join(", ") : String(value || "");
  if (field.input === "json") {
    if (value === null || value === undefined) return "";
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return "";
    }
  }
  return value === null || value === undefined ? "" : String(value);
};

// Thrown for an unparsable "json"-input field; callers (submit()) catch this
// to show a field-specific error instead of silently dropping or corrupting
// the value (see admin.id.express/AGENTS.md on not rendering/saving objects
// as opaque strings).
export class InvalidMetadataJsonError extends Error {
  constructor(public readonly path: string) {
    super(`Invalid JSON for ${path}`);
  }
}

const typedValue = (value: string | boolean, field: DictionaryMetadataField) => {
  if (field.input === "checkbox") return Boolean(value);
  const text = String(value).trim();
  if (!text) return undefined;
  if (field.input === "tags") return text.split(",").map(item => item.trim()).filter(Boolean);
  if (field.input === "number") return field.bsonType === "int" ? Number.parseInt(text, 10) : Number(text);
  if (field.input === "json") {
    try {
      return JSON.parse(text);
    } catch {
      throw new InvalidMetadataJsonError(field.path);
    }
  }
  return text;
};

export const editableMetadata = (metadata: Record<string, any>, schema: DictionaryFormSchema) =>
  Object.fromEntries((schema.metadataFields || []).map(field => [
    field.path,
    inputValue(valueAtPath(metadata, field.path), field),
  ]));

export const mergeEditableMetadata = (
  original: Record<string, any>,
  values: Record<string, string | boolean>,
  schema: DictionaryFormSchema,
) => {
  const metadata = JSON.parse(JSON.stringify(original || {}));
  (schema.metadataFields || []).forEach(field =>
    setAtPath(metadata, field.path, typedValue(values[field.path] ?? "", field)));
  return metadata;
};

export default function DictionaryPanel({
  rows, datasetKey, resource, categories, languages, methods, canCreate, createRequest,
  schemas, onLoadSchema, onOpenCategory, onSave, onDelete, onFindParents, ...tableProps
}: DictionaryPanelProps) {
  const {t} = useTranslation();
  const [editing, setEditing] = useState<DictionaryRecord | null>(null);
  const [form, setForm] = useState(() => emptyForm(resource === "create" ? categories[0] : resource));
  const [baseline, setBaseline] = useState(form);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [parentOptions, setParentOptions] = useState<ParentOption[]>([]);
  const [parentLoading, setParentLoading] = useState(false);
  const lastCreateRequest = useRef(createRequest);
  const creating = !editing?._id;
  const draftKey = `admin-id-express-dictionary-draft:${form.type}:${editing?._id || "create"}`;
  const schema = schemas[form.type] || {type: form.type, metadataFields: []};
  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(baseline), [form, baseline]);

  const close = () => {
    if (dirty && !window.confirm(t("forms.discardChanges"))) return;
    setEditing(null);
    setError("");
  };
  const dialogRef = useDialogFocus<HTMLFormElement>(Boolean(editing), close);

  const restoreDraft = (initial: ReturnType<typeof emptyForm>, key: string) => {
    try {
      const draft = JSON.parse(localStorage.getItem(key) || "null");
      return draft && typeof draft === "object" ? {...initial, ...draft} : initial;
    } catch {
      return initial;
    }
  };

  const openCreate = (type: string) => {
    const nextSchema = schemas[type] || {type, metadataFields: []};
    const initial = {...emptyForm(type), metadataValues: editableMetadata({}, nextSchema)};
    setBaseline(initial);
    setForm(restoreDraft(initial, `admin-id-express-dictionary-draft:${type}:create`));
    setError("");
    setEditing({});
    if (!schemas[type]) void onLoadSchema(type).catch(() => undefined);
  };

  useEffect(() => {
    if (createRequest === lastCreateRequest.current || resource === "create" && !categories.length) return;
    lastCreateRequest.current = createRequest;
    openCreate(resource === "create" ? categories[0] : resource);
  }, [createRequest]);

  useEffect(() => {
    if (editing && dirty) localStorage.setItem(draftKey, JSON.stringify(form));
  }, [form, editing, dirty, draftKey]);

  useEffect(() => {
    if (!editing) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [editing, dirty]);

  useEffect(() => {
    if (!editing || !form.parentType) { setParentOptions([]); return; }
    let active = true;
    const timer = window.setTimeout(() => {
      setParentLoading(true);
      onFindParents(form.parentType, form.parentSearch)
        .then(options => { if (active) setParentOptions(options); })
        .catch(() => { if (active) setParentOptions([]); })
        .finally(() => { if (active) setParentLoading(false); });
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [editing, form.parentType, form.parentSearch, onFindParents]);

  const openEdit = async (record: DictionaryRecord) => {
    const type = record.type || resource;
    let nextSchema = schemas[type];
    let schemaError = "";
    if (!nextSchema) {
      try {
        nextSchema = await onLoadSchema(type);
      } catch {
        // Same graceful fallback as openCreate: don't let a transient schema
        // fetch failure silently no-op the whole Edit action — still open
        // the dialog (without metadata fields) and surface the error.
        nextSchema = {type, metadataFields: []};
        schemaError = t("unableLoad");
      }
    }
    const locales = Object.entries(record.value || {}).map(([locale, value]: [string, any]) => ({
      locale: normalizeLanguage(locale) || locale, name: String(value?.name || ""), short: String(value?.short || ""),
    }));
    const initial = {
      ...emptyForm(type), type, key: record.key || "",
      locales: locales.length ? locales : emptyForm(type).locales,
      parentType: PARENT_TYPES[type]?.[0] || "",
      parentId: record.parentId || "", tags: (record.tags || []).join(", "),
      metadataValues: editableMetadata(record.metadata || {}, nextSchema),
      status: record.status || "enabled", sortOrder: String(record.sortOrder ?? 0),
      effectiveFrom: dateInputValue(record.effectiveFrom), effectiveTo: dateInputValue(record.effectiveTo),
    };
    setBaseline(initial);
    setForm(restoreDraft(initial, `admin-id-express-dictionary-draft:${type}:${record._id}`));
    setError(schemaError);
    setEditing(record);
  };

  const changeType = async (type: string) => {
    const nextSchema = schemas[type] || await onLoadSchema(type);
    setForm(current => ({
      ...current, type, parentType: PARENT_TYPES[type]?.[0] || "", parentId: "", parentSearch: "",
      metadataValues: editableMetadata({}, nextSchema),
    }));
  };
  const updateLocale = (index: number, patch: Partial<LocaleRow>) =>
    setForm(current => ({...current, locales: current.locales.map((row, rowIndex) => rowIndex === index ? {...row, ...patch} : row)}));
  const removeLocale = (index: number) =>
    setForm(current => ({...current, locales: current.locales.filter((_, rowIndex) => rowIndex !== index)}));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validLocales = form.locales.filter(row => row.locale && row.name.trim());
    if (!form.type || !form.key.trim() || !validLocales.length) { setError(t("forms.required")); return; }
    if (["region", "city"].includes(form.type) && !form.parentId) {
      setError(t("dictionary.parentRequired")); return;
    }
    const value = Object.fromEntries(validLocales.map(row => [
      row.locale,
      {name: row.name.trim(), ...(row.short.trim() ? {short: row.short.trim()} : {})},
    ]));
    let metadata: Record<string, any>;
    try {
      metadata = mergeEditableMetadata(editing?.metadata || {}, form.metadataValues, schema);
    } catch (metadataError) {
      if (metadataError instanceof InvalidMetadataJsonError) {
        setError(t("dictionary.invalidJsonField", {
          path: metadataError.path,
          defaultValue: `Invalid JSON in "${metadataError.path}".`,
        }));
        return;
      }
      throw metadataError;
    }
    setSaving(true);
    setError("");
    try {
      await onSave(resource, {
        ...(editing?._id ? {_id: editing._id} : {}), type: form.type,
        key: form.key.trim(), value, parentId: form.parentId || null,
        tags: form.tags.split(",").map(tag => tag.trim()).filter(Boolean),
        metadata, status: form.status, sortOrder: Number(form.sortOrder) || 0,
        effectiveFrom: form.effectiveFrom ? new Date(form.effectiveFrom).toISOString() : null,
        effectiveTo: form.effectiveTo ? new Date(form.effectiveTo).toISOString() : null,
      }, creating);
      localStorage.removeItem(draftKey);
      setEditing(null);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || t("unableLoad"));
    } finally { setSaving(false); }
  };

  const remove = async (record: DictionaryRecord, closeAfter = false) => {
    if (!window.confirm(t("dictionary.confirmDelete"))) return;
    try {
      await onDelete(record.type || resource, record);
      if (closeAfter) {
        localStorage.removeItem(draftKey);
        setEditing(null);
      }
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || t("unableLoad"));
    }
  };
  const parentTypes = Array.from(new Set([...(PARENT_TYPES[form.type] || []), ...categories.filter(category => category !== form.type)]));
  const directoryActions = (): IMenuAction[] => [
    {id: "open", label: t("dictionary.openCategory"), icon: "eye"},
    ...(canCreate ? [
      {type: "divider" as const},
      {id: "add", label: t("dictionary.addRecord"), icon: "plus"},
    ] : []),
  ];
  const handleDirectoryAction = (action: string, category: string) => {
    if (action === "open") onOpenCategory(category);
    if (action === "add") openCreate(category);
  };

  return <>
    {resource === "create" ? <DataTable {...tableProps} rows={rows} datasetKey={datasetKey}
      defaultColumns={["name", "type"]}
      filters={[
        {field: "name", label: t("dictionary.fields.name"), type: "text"},
        {field: "type", label: t("dictionary.fields.type"), type: "text"},
      ]}
      includeViewAction={false}
      rowActions={directoryActions}
      onRowAction={(action, record) => handleDirectoryAction(action, String(record.type || ""))}/> :
    <DataTable {...tableProps} rows={rows} datasetKey={datasetKey}
      defaultColumns={["key", "value.default.name", "status", "metadata"]}
      rowActions={() => methods.includes("PUT") ? [{id: "edit", label: t("dictionary.edit"), icon: "edit"}, ...(methods.includes("DELETE") ? [{id: "delete", label: t("dictionary.delete"), icon: "delete", isDanger: true} as const] : [])] : []}
      onRowAction={(action, record) => action === "edit" ? void openEdit(record) : action === "delete" ? void remove(record) : undefined}/>}

    {editing && <div className="modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) close(); }}>
      <form ref={dialogRef} tabIndex={-1} className="modal dictionary-form" role="dialog" aria-modal="true" aria-labelledby="dictionary-form-title" onSubmit={submit}>
        <button type="button" className="modal-close" title={t("administrators.close")} aria-label={t("administrators.close")} onClick={close}>×</button>
        <span className="eyebrow">{t("dictionaryService")}</span>
        <h2 id="dictionary-form-title">{creating ? t("dictionary.addRecord") : t("dictionary.edit")}</h2>
        {error && <div className="alert sticky-form-error" role="alert">{error}</div>}
        <div className="form-grid">
          <label>{t("dictionary.fields.type")}<select data-autofocus value={form.type} disabled={!creating || resource !== "create"} onChange={event => void changeType(event.target.value)}>{categories.map(category => <option key={category} value={category}>{t(`dictionary.resources.${category}`, {defaultValue: category})}</option>)}</select></label>
          <label>{t("dictionary.fields.key")}<input value={form.key} disabled={!creating} onChange={event => setForm({...form, key: event.target.value})} required/></label>

          <fieldset className="form-wide dictionary-fieldset">
            <legend>{t("dictionary.fields.translations")}</legend>
            {form.locales.map((row, index) => <div className="dictionary-row dictionary-locale-row" key={`${row.locale}:${index}`}>
              <select value={row.locale} onChange={event => updateLocale(index, {locale: event.target.value})}>
                <option value="default">{t("dictionary.defaultLanguage")}</option>
                {languages.map(language => <option key={language} value={language}>{language.toUpperCase()}</option>)}
              </select>
              <input value={row.name} placeholder={t("dictionary.fields.name")} aria-label={t("dictionary.fields.name")} onChange={event => updateLocale(index, {name: event.target.value})}/>
              <input value={row.short} placeholder={t("dictionary.fields.short")} aria-label={t("dictionary.fields.short")} onChange={event => updateLocale(index, {short: event.target.value})}/>
              <button type="button" className="secondary icon-button" disabled={form.locales.length === 1} title={t("dictionary.removeRow")} onClick={() => removeLocale(index)}>×</button>
            </div>)}
            <button type="button" className="secondary compact-button" title={t("dictionary.addTranslation")} onClick={() => setForm(current => ({...current, locales: [...current.locales, {locale: languages.find(language => !current.locales.some(row => row.locale === language)) || "en", name: "", short: ""}]}))}>+ {t("dictionary.addTranslation")}</button>
          </fieldset>

          <fieldset className="form-wide dictionary-fieldset">
            <legend>{t("dictionary.fields.parent")}</legend>
            <div className="dictionary-row dictionary-parent-row">
              <select value={form.parentType} onChange={event => setForm({...form, parentType: event.target.value, parentId: "", parentSearch: ""})}>
                <option value="">{t("dictionary.noParent")}</option>
                {parentTypes.map(type => <option key={type} value={type}>{t(`dictionary.resources.${type}`, {defaultValue: type})}</option>)}
              </select>
              <input type="search" disabled={!form.parentType} value={form.parentSearch} placeholder={t("dictionary.searchParent")} onChange={event => setForm({...form, parentSearch: event.target.value})}/>
              <select disabled={!form.parentType || parentLoading} value={form.parentId} onChange={event => setForm({...form, parentId: event.target.value})}>
                <option value="">{parentLoading ? t("opening") : t("dictionary.selectParent")}</option>
                {form.parentId && !parentOptions.some(option => option.id === form.parentId) && <option value={form.parentId}>{form.parentId}</option>}
                {parentOptions.map(option => <option key={option.id} value={option.id}>{option.name} · {option.key}</option>)}
              </select>
            </div>
          </fieldset>

          <label className="form-wide">{t("dictionary.fields.tags")}<input value={form.tags} placeholder={t("dictionary.tagsHint")} onChange={event => setForm({...form, tags: event.target.value})}/></label>

          {(schema.metadataFields || []).length > 0 && <fieldset className="form-wide dictionary-fieldset dictionary-schema-fields">
            <legend>{t("dictionary.fields.metadata")}</legend>
            {(schema.metadataFields || []).map(field => field.input === "checkbox" ?
              <label className="dictionary-boolean-field" key={field.path}>
                <input type="checkbox" checked={Boolean(form.metadataValues[field.path])} onChange={event => setForm(current => ({...current, metadataValues: {...current.metadataValues, [field.path]: event.target.checked}}))}/>
                <span>{t(`dictionary.metadataFields.${field.path}`, {defaultValue: field.description || field.path})}</span>
              </label> :
              <label key={field.path}>{t(`dictionary.metadataFields.${field.path}`, {defaultValue: field.description || field.path})}
                {field.input === "json" ?
                  <textarea className="dictionary-json-field" rows={4} spellCheck={false} value={String(form.metadataValues[field.path] ?? "")} onChange={event => setForm(current => ({...current, metadataValues: {...current.metadataValues, [field.path]: event.target.value}}))}/> :
                  <input type={field.input === "number" ? "number" : field.input === "url" ? "url" : "text"} step={field.step} value={String(form.metadataValues[field.path] ?? "")} onChange={event => setForm(current => ({...current, metadataValues: {...current.metadataValues, [field.path]: event.target.value}}))}/>}
              </label>)}
          </fieldset>}

          <label>{t("dictionary.fields.status")}<select value={form.status} onChange={event => setForm({...form, status: event.target.value})}><option value="enabled">enabled</option><option value="disabled">disabled</option><option value="deprecated">deprecated</option></select></label>
          <label>{t("dictionary.fields.sortOrder")}<input type="number" value={form.sortOrder} onChange={event => setForm({...form, sortOrder: event.target.value})}/></label>
          <label>{t("dictionary.fields.effectiveFrom")}<input type="datetime-local" value={form.effectiveFrom} onChange={event => setForm({...form, effectiveFrom: event.target.value})}/></label>
          <label>{t("dictionary.fields.effectiveTo")}<input type="datetime-local" value={form.effectiveTo} onChange={event => setForm({...form, effectiveTo: event.target.value})}/></label>
        </div>
        <div className="modal-actions">
          {!creating && methods.includes("DELETE") && <button type="button" className="danger-button modal-delete" title={t("dictionary.delete")} onClick={() => void remove(editing, true)}>{t("dictionary.delete")}</button>}
          <button type="button" className="secondary" onClick={close}>{t("forms.cancel")}</button>
          <button type="submit" className="primary" disabled={saving}>{saving ? t("opening") : t("forms.save")}</button>
        </div>
      </form>
    </div>}
  </>;
}
